import { Box, Button, Flex, HStack, Icon, Image, Text, useToast } from "@chakra-ui/react"
import HoverableIconButton from "components/Atomos/Buttons/HoverableIconButton"
import { Filter } from "components/Atomos/Filter/Base"
import DatePeriodFilter from "components/Atomos/Filter/Base/DatePeriodFilter"
import { DropdownFilter } from "components/Atomos/Filter/Base/DropdownFilter"
import NoImage from "components/Atomos/NoImage"
import { DraftStatusTag } from "components/Atomos/Tags/DraftStatusTag"
import { Td, Th, Tr } from "components/Moleculas/ResponsiveTable"
import DeleteButton from "components/Organismos/Popovers/DeleteButton"
import PublishButton from "components/Organismos/Popovers/PublishButton"
import TablePageTemplate from "components/Templates/TablePage"
import { Breadcrumb } from "layouts/LayoutDefault/Breadcrumb"
import NewPostsPage from "pages/Posts/new-posts"
import { useCallback, useEffect, useState } from "react"
import { FiEdit } from "react-icons/fi"
import { RiAddFill } from "react-icons/ri"
import { useAppDispatch, useTypedSelector } from "store/hooks"
import { deletePostRequest } from "store/modules/posts/actions/deletePost"
import { getPostsPaginatedRequest } from "store/modules/posts/actions/getPostsPaginated"
import { publishPostRequest } from "store/modules/posts/actions/publishPost"
import { PostagemPaged } from "store/modules/posts/types"
import { useDispatchToPromise } from "utils/dispatch-to-promise"
import { useQueryParam } from "utils/hooks/useQueryParam"
import { wait } from "utils/wait"

const PostsPage = () => {
    const [search, setSearch] = useState('');
    const [, setShowPostForm] = useQueryParam<boolean>('showPostForm');
    const [, setPostId] = useQueryParam<string>('postId');
    const [onlyPublished] = useQueryParam<string>('only_published');
    const [postType] = useQueryParam<'PONTO_ESSENCIAL' | 'INFORMATIVO' | undefined>('post_type');
    const [startDate, setStartDate] = useQueryParam<string>('start_date');
    const [endDate, setEndDate] = useQueryParam<string>('end_date');
    const [page, setPage] = useState(0);
    const dispatch = useAppDispatch();
    const toast = useToast();

    const fetchData = useCallback((
        index: number,
        pageSize: number,
        search?: string,
        startDate?: string,
        endDate?: string,
        postType?: "INFORMATIVO" | "PONTO_ESSENCIAL",
        onlyPublished?: boolean
    ) => {
        if (!postType) {
            toast({
                description: 'Selecione um tipo de postagem'
            });
            return;
        }

        dispatch(getPostsPaginatedRequest({
            index,
            pageSize,
            search,
            startDate: !!startDate ? new Date(startDate) : undefined,
            endDate: !!endDate ? new Date(endDate) : undefined,
            postType,
            onlyPublished
        }))
    }, [dispatch]);

    useEffect(() => {
        fetchData(page, 10, search, startDate, endDate, postType, onlyPublished === "true");
    }, [fetchData, page, startDate, endDate, postType, onlyPublished, search]);

    const {
        successPayload,
        isRequesting
    } = useTypedSelector(state => (
        state.posts.getPostsPaginated
    ));

    const openNewPostModal = () => {
        setShowPostForm(true);
    }

    const dispatchAsPromise = useDispatchToPromise();

    const publishPost = async (post: PostagemPaged) => {
        await dispatchAsPromise(publishPostRequest, {
            postId: post.id,
            postType: postType!,
            willPublish: !post.publicado
        });
        toast({
            description: `Postagem ${post.publicado ? "ocultada" : "publicada com sucesso"}`
        });
        fetchData(page, 10, search, startDate, endDate, postType, onlyPublished === "true");
    }

    const deletePost = async (postId: string) => {
        await dispatchAsPromise(deletePostRequest, {
            postId,
            postType: postType!
        });
        toast({
            status: 'info',
            description: `Postagem removida com sucesso`
        });

        fetchData(page, 10, search, startDate, endDate, postType, onlyPublished === "true");
    }

    return (
        <Box p={8}>
            <Breadcrumb paths={[{ name: "Início", link: '/' }, { name: 'Publicações' }]} />
            <TablePageTemplate
                title="Publicações"
                newItemButton={(
                    <Button leftIcon={<Icon as={RiAddFill} />} onClick={openNewPostModal}>Nova publicação</Button>
                )}
                onSearch={setSearch}
                filters={(
                    <Flex flexDir="column">
                        <Text fontSize="sm">Clique para <b>selecionar</b> o tipo de postagem</Text>
                        <HStack>
                            <DropdownFilter
                                label="Tipo de postagem"
                                name="post_type"
                                options={[
                                    { value: 'INFORMATIVO', name: 'Informativo' },
                                    { value: 'PONTO_ESSENCIAL', name: 'Ponto essencial' }
                                ]}
                            />
                            <DatePeriodFilter
                                onChange={(start, end) => {
                                    setStartDate(start?.toLocaleDateString('pt-BR').split('/')?.reverse()?.join('-') ?? '');
                                    setEndDate(end?.toLocaleDateString('pt-BR').split('/')?.reverse()?.join('-') ?? '');
                                }}
                            />
                            <Filter name="only_published">Apenas publicados</Filter>
                        </HStack>
                    </Flex>
                )}
                tableHeader={(
                    <Tr>
                        <Th>Status da publicação</Th>
                        <Th>Imagem</Th>
                        <Th>Título</Th>
                        <Th>Data de publicação</Th>
                        <Th>Endereço</Th>
                        <Th></Th>
                    </Tr>
                )}
                renderTableItem={(item) => (
                    <Tr>
                        <Td>
                            <DraftStatusTag active={item.publicado} />
                        </Td>
                        <Td>
                            <Image
                                boxSize={12}
                                rounded="full"
                                src={`${process.env.REACT_APP_API}/${postType === "INFORMATIVO" ? "informativos" : "pontosEssenciais"}/imagem/${item.id}?stamp=${Date.now()}`}
                                alt={`Imagem da publicação ${item.titulo}`}
                                fallback={<NoImage/>}
                            />
                        </Td>
                        <Td>{item.titulo}</Td>
                        <Td>
                            {item.dataPublicacao && (
                                <Text>{new Date(item.dataPublicacao.split('+')[0])?.toLocaleDateString('pt-BR')}</Text>
                            )}
                            {!item.dataPublicacao && (
                                <Text>-</Text>
                            )}
                        </Td>
                        <Td>
                            {item.endereco.logradouro}, {item.endereco.bairro}, {item.endereco.municipio} - {item.endereco.uf}
                        </Td>
                        <HStack as={Td}>
                            <PublishButton
                                isInDraft={!item.publicado}
                                onConfirm={() => publishPost(item)}
                            />
                            <HoverableIconButton
                                label="Editar"
                                icon={<Icon as={FiEdit} />}
                                onClick={async () => {
                                    openNewPostModal();
                                    await wait(200)
                                    setPostId(item.id);
                                }}
                            />
                            <DeleteButton
                                onConfirm={() => deletePost(item.id)}
                            />
                        </HStack>
                    </Tr>
                )}
                data={!postType ? [] as PostagemPaged[] : successPayload.items}
                onPageChange={setPage}
                pageProps={{
                    index: page,
                    pageSize: successPayload.pageSize,
                    totalItems: successPayload.totalCount,
                    hasNextPage: successPayload.hasNextPage,
                    hasPreviousPage: successPayload.hasPreviousPage,
                    isLoading: isRequesting
                }}
            />
            <NewPostsPage />
        </Box>
    )
}

export default PostsPage